import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  makeStyles,
  Select,
  TextField
} from '@material-ui/core';

import TimeSeriesSetIdSelector from 'components/Widget/components/menus/MainSettings/components/TimeSeriesSetIdSelector';
import useFetchMasterObjects from 'hooks/useFetchMasterObjects';
import TabPanel from 'components/TabPanel';
import DropdownAutocomplete from 'components/DropdownAutocomplete';
import { ThemeButton } from 'components/shared';
import useFetchTimeSeriesSet from 'hooks/useApi/useFetchTimeSeriesSets';
import LoadingSpinner from 'components/LoadingSpinner';
import MenuControls from './MenuControls';
import AdvancedSearch from './AdvancedSearch';

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: '700px'
  },
  selectRoot: {
    '&:hover': {
      color: theme.palette.primary.main,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.accent.main
      }
    }
  },
  selectInput: {
    padding: '10px'
  },
  defaultFieldInput: {
    padding: '5px',
    color: theme.palette.grey.dark,
    border: `1px solid ${theme.palette.grey.medium}`,
    borderRadius: '4px',
    marginBottom: '8px',
    '&:before': {
      display: 'none'
    },
    '&:after': {
      display: 'none'
    }
  }
}));

const masterObjectTypes = [
  { id: 1, name: 'Portfolio' },
  { id: 2, name: 'Peer Group' },
  { id: 3, name: 'Holding' }
];

const MasterObjectTab = ({
  value,
  onSubmit,
  handleClose,
  masterObjectId,
  masterObjectTypeId,
  dashboardData,
  timeSeriesSetId: widgetTimeSeriesSetId
}) => {
  const classes = useStyles();
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);
  const [masterObjectIdState, setMasterObjectIdState] = useState(masterObjectId);
  const [timeSeriesSetId, setTimeSeriesSetId] = useState(widgetTimeSeriesSetId);
  const [options] = useFetchTimeSeriesSet();

  const [
    masterObjects,
    masterObjectTypeIdState,
    setMasterObjectTypeIdState
  ] = useFetchMasterObjects({ masterObjectTypeId: masterObjectTypeId ?? 1 });

  const handleSubmit = () => {
    const formData = {
      masterObjectId: masterObjectIdState,
      masterObjectTypeId: masterObjectTypeIdState,
      timeSeriesSetId
    };
    onSubmit(formData);
  };

  const handleTimeSeriesSetSelect = id => {
    setTimeSeriesSetId(id);
  };

  const handleMasterObjectIdChange = selected => {
    setMasterObjectIdState(selected.value);
  };

  const handleMasterObjectTypeChange = event => {
    setMasterObjectTypeIdState(event.target.value);
  };

  const toggleAdvancedSearch = () => {
    setIsAdvancedSearch(!isAdvancedSearch);
  };

  const getDefaultMasterObjectType = () => {
    const selectedType = masterObjectTypes.find(m => m.id === dashboardData.masterObjectTypeId);
    if (!selectedType) {
      return '';
    }

    return selectedType.name || '';
  };

  const getDefaultTimeSeriesSetId = () => {
    const selectedType = options.find(m => m.value === dashboardData.timeSeriesSetId);
    if (!selectedType) {
      return '';
    }

    return selectedType.label || '';
  };

  return (
    <TabPanel value={value} index={1} style={{ minWidth: '700px' }}>
      <Box className={classes.root} p={2}>
        <Grid container direction="row" justify="center" alignItems="flex-start" spacing={2}>
          <Grid item xs={12} md={5}>
            <h3>Default Object type</h3>
            <TextField
              value={getDefaultMasterObjectType()}
              InputProps={{ className: classes.defaultFieldInput }}
              disabled
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <h3>Object type</h3>
            <FormControl variant="outlined" fullWidth>
              <InputLabel htmlFor="object-type-select">Item(s)...</InputLabel>
              <Select
                native
                value={masterObjectTypeIdState}
                onChange={handleMasterObjectTypeChange}
                className={classes.selectRoot}
                classes={{ select: classes.selectInput }}
                label="Choose..."
                inputProps={{ id: 'object-type-select' }}
              >
                {masterObjectTypes.map(m => (
                  <option key={m.name} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={5}>
            <h3>Default Object ID</h3>
            <TextField
              defaultValue={dashboardData.masterObjectName}
              InputProps={{ className: classes.defaultFieldInput }}
              disabled
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <h3>Choose Object ID</h3>
            <Grid item container direction="row" spacing={1}>
              <Grid item xs={9}>
                {masterObjects ? (
                  <DropdownAutocomplete
                    selected={masterObjectIdState}
                    options={masterObjects}
                    onSelect={handleMasterObjectIdChange}
                    minWidth="220px"
                    type="seriesType"
                    listBoxHeight="360px"
                  />
                ) : (
                  <LoadingSpinner p={0} size={40} />
                )}
              </Grid>
              <Grid item xs={3}>
                <ThemeButton
                  themeColor={isAdvancedSearch ? 'grey' : 'accent'}
                  size="small"
                  onClick={toggleAdvancedSearch}
                  style={{ padding: '5px', fontSize: '15px' }}
                >
                  {isAdvancedSearch ? 'Hide' : 'Advanced'}
                </ThemeButton>
              </Grid>
            </Grid>
            {isAdvancedSearch && <AdvancedSearch />}
          </Grid>

          <Grid item xs={12} md={5}>
            <h3>Default Peer Group</h3>
            <TextField
              defaultValue={getDefaultTimeSeriesSetId()}
              InputProps={{ className: classes.defaultFieldInput }}
              disabled
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <h3>Widget Peer Group</h3>
            <Grid item xs={12}>
              <TimeSeriesSetIdSelector
                dashboardData={dashboardData}
                handleSelect={handleTimeSeriesSetSelect}
                timeSeriesSetId={timeSeriesSetId}
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <MenuControls handleClose={handleClose} useFormSubmit={false} onSubmit={handleSubmit} />
    </TabPanel>
  );
};

MasterObjectTab.propTypes = {
  value: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  masterObjectId: PropTypes.number.isRequired,
  masterObjectTypeId: PropTypes.number.isRequired,
  dashboardData: PropTypes.shape({
    masterObjectId: PropTypes.number.isRequired,
    masterObjectTypeId: PropTypes.number.isRequired,
    masterObjectName: PropTypes.string.isRequired,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        benchmarkId: PropTypes.number.isRequired
      })
    )
  })
};

MasterObjectTab.defaultProps = {
  dashboardData: {}
};

export default MasterObjectTab;
